import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Loading } from "@Components/Loading";
import { Ajax } from "@Components/Modules/Ajax";
import type { ISearchBoxResult, ISearchBoxResultItem } from "@Components/SearchBox";
import { SearchBox } from "@Components/SearchBox";
import { TextboxAppearance } from "@Components/Textbox";
import { SearchCity } from "@Routes/LocationLinks";

import * as css from "./PlaceSearch.module.css";

export interface IPlaceSearch extends IComponentAjax<ISearchBoxResult> {
    actionUrl?: string;
    onSelect?: (event: React.MouseEvent<HTMLAnchorElement>, item: ISearchBoxResultItem) => boolean;
    hideResults?: boolean;
    onChange?: (value: string) => boolean;
    onMatch?: (items: ISearchBoxResultItem[]) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>, item: ISearchBoxResultItem) => boolean | void;
    value?: string;
    watermarkText?: string;
}

interface IPlaceSearchState {
    searchResult: ISearchBoxResult;
    isLoading: boolean;
    searchValue: string;
}

export class PlaceSearch extends ComponentAjax<ISearchBoxResult, IPlaceSearch, IPlaceSearchState> {
    public static readonly defaultProps: IPlaceSearch = {
        ajaxAction: SearchCity.url,
        hideResults: false,
    };

    constructor(props: IPlaceSearch) {
        super(props);

        this.state = {
            searchResult: { matches: [] },
            isLoading: false,
            searchValue: props.value || "",
        };
    }

    public componentDidMount() {
        if (!this.ajax) {
            this.ajax = new Ajax(this.props);
        }
    }

    public get firstLocationMatch() {
        return (
            this.state.searchResult && this.state.searchResult.matches && this.state.searchResult.matches[0]
        );
    }

    private search = (value: string): boolean => {
        this.setState(() => ({ searchValue: value }));

        if (value.length > 3 && this.ajax) {
            this.ajax.ajaxAction = SearchCity.url;
            this.ajaxRequest({ name: value });
        }

        return true;
    };

    protected ajaxSuccess(response: ISearchBoxResult): void {
        if (this.props.onMatch) {
            this.props.onMatch(response.matches.slice());
        }

        if (this.props.hideResults === true) {
            return;
        }

        this.setState({ searchResult: response, isLoading: false });
    }

    protected ajaxFail() {
        this.setState({ isLoading: false });
        return true;
    }

    private onSelect = (event: React.MouseEvent<HTMLAnchorElement>, item: ISearchBoxResultItem): boolean => {
        if (!item || !item.text) {
            return false;
        }

        const val = (this.props.onSelect && this.props.onSelect(event, item)) || false;

        this.setState(() => ({
            searchValue: item.text,
            isLoading: true,
        }));

        return val;
    };

    private onChange = (event: React.ChangeEvent<HTMLInputElement>) => this.search(event.target.value);

    public render() {
        return (
            <div {...defaultAttributes(this.props, css.placeSearchComponent)}>
                {this.state.isLoading && <Loading isModal />}
                <SearchBox
                    {...SearchBox.defaultProps}
                    value={this.state.searchValue}
                    onChange={this.onChange}
                    initialData={this.state.searchResult}
                    onSelect={this.onSelect}
                    appearance={TextboxAppearance.Standard}
                    watermarkText={this.props.watermarkText}
                    hideResults={this.props.hideResults}
                    onKeyDown={this.props.onKeyDown}
                    onFocus={this.props.onFocus}
                />
                <span>
                    <strong />
                </span>
            </div>
        );
    }
}
